import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { Subscription, interval, startWith, switchMap } from 'rxjs';
import { environment } from 'environments/environment';
import { HttpClient } from '@angular/common/http';

interface PacienteStats {
    total: number;
    newThisMonth: number;
    conRecetaReciente?: number;
    conRecetaAnio?: number;
    sinRecetaReciente?: number;
}

@Component({
    selector: 'receta-badge',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Default,
    imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule, MatMenuModule],
    template: `
        <button
            mat-icon-button
            [matMenuTriggerFor]="menu"
            [matTooltip]="tooltip"
            class="relative"
            data-testid="receta-badge">
            <mat-icon [svgIcon]="'heroicons_outline:document-text'"></mat-icon>
            @if (badgeNumber > 0) {
                <span
                    class="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white shadow"
                    [ngClass]="badgeClass"
                    data-testid="receta-badge-count">
                    {{ badgeNumber > 99 ? '99+' : badgeNumber }}
                </span>
            }
        </button>
        <mat-menu #menu="matMenu" class="receta-menu">
            <div class="px-4 py-3 min-w-[260px]" data-testid="receta-menu">
                <div class="text-sm font-semibold text-gray-700 mb-2">Seguimiento de Recetas</div>
                <div class="flex justify-between text-xs py-1">
                    <span class="text-gray-500">Últimos 30 días:</span>
                    <span class="font-medium text-green-700" data-testid="receta-con-reciente">
                        {{ conRecetaReciente }}
                    </span>
                </div>
                <div class="flex justify-between text-xs py-1">
                    <span class="text-gray-500">Último año:</span>
                    <span class="font-medium text-blue-700" data-testid="receta-con-anio">
                        {{ conRecetaAnio }}
                    </span>
                </div>
                <div class="flex justify-between text-xs py-1 border-t pt-2 mt-1">
                    <span class="text-gray-500">Sin receta reciente:</span>
                    <span
                        class="font-medium"
                        [ngClass]="sinRecetaReciente > 50 ? 'text-red-600' : 'text-amber-600'"
                        data-testid="receta-sin-reciente">
                        {{ sinRecetaReciente }}
                    </span>
                </div>
                <div class="flex gap-2 mt-3">
                    <button
                        mat-stroked-button
                        color="primary"
                        class="flex-1"
                        (click)="irARecetas()"
                        data-testid="receta-menu-list">
                        <mat-icon class="icon-size-4 mr-1">list</mat-icon>
                        Ver Recetas
                    </button>
                    <button
                        mat-stroked-button
                        class="flex-1"
                        (click)="irAPacientes()"
                        data-testid="receta-menu-pacientes">
                        <mat-icon class="icon-size-4 mr-1">people</mat-icon>
                        Pacientes
                    </button>
                </div>
            </div>
        </mat-menu>
    `,
})
export class RecetaBadgeComponent implements OnInit, OnDestroy {
    total = 0;
    conRecetaReciente = 0;
    conRecetaAnio = 0;
    sinRecetaReciente = 0;
    cargando = true;
    error = false;

    private pollSub?: Subscription;

    constructor(
        private _http: HttpClient,
        private _router: Router,
        private _cdr: ChangeDetectorRef,
    ) {}

    get badgeNumber(): number {
        // Mostrar el contador más accionable:
        // pacientes SIN receta reciente (los que requieren seguimiento).
        return this.sinRecetaReciente;
    }

    get badgeClass(): string {
        if (this.sinRecetaReciente >= 200) return 'bg-red-600';
        if (this.sinRecetaReciente >= 50) return 'bg-amber-500';
        return 'bg-emerald-500';
    }

    get tooltip(): string {
        if (this.cargando) return 'Cargando seguimiento de recetas…';
        if (this.error) return 'Error al cargar seguimiento de recetas';
        return `${this.sinRecetaReciente} pacientes sin receta reciente — click para ver seguimiento`;
    }

    ngOnInit(): void {
        const url = `${environment.urlBase}paciente/stats`;
        this.pollSub = interval(120_000)
            .pipe(
                startWith(0),
                switchMap(() => this._http.get<PacienteStats>(url)),
            )
            .subscribe({
                next: (s) => {
                    this.cargando = false;
                    this.error = false;
                    this.total = s?.total ?? 0;
                    this.conRecetaReciente = s?.conRecetaReciente ?? 0;
                    this.conRecetaAnio = s?.conRecetaAnio ?? 0;
                    this.sinRecetaReciente = s?.sinRecetaReciente ?? Math.max(0, this.total - this.conRecetaAnio);
                    this._cdr.detectChanges();
                },
                error: () => {
                    this.cargando = false;
                    this.error = true;
                    this._cdr.detectChanges();
                },
            });
    }

    irARecetas(): void {
        this._router.navigate(['/apps/receta/list']);
    }

    irAPacientes(): void {
        this._router.navigate(['/apps/paciente/list']);
    }

    ngOnDestroy(): void {
        this.pollSub?.unsubscribe();
    }
}
